// Activate Account — Step 1: Set Display Name + Password
// -------------------------------------------------------
// Functional component with Tailwind styling. Part of the dark-themed
// auth flow (ActivateAccount → ActivatePageOne → ActivatePageTwo).

import React, { useState, useCallback } from "react";
import { Check, Loader2, Lock, User } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import { EMPTY_STRING, MFA_SETUP } from "@/constants/definitions";
import CognitoLoginClass from "@/models/cognitoLogin";
import PasswordValidationClass, { PASSWORD_REQUIREMENTS } from "@/utils/passwordValidation";

interface ActivatePageOneProps {
  cognitologin: CognitoLoginClass;
  username: string;
  oldpassword?: string;
  pageOneStateChangeHandler: (password: string) => void;
  updateDisplayName: (displayName: string) => void;
  /** Called instead of advancing to the avatar step when the pool requires MFA:
   *  completeNewPassword returns an MFA_SETUP challenge (no session yet), so the
   *  user must enrol a TOTP factor before any authenticated call (e.g. avatar
   *  upload) can succeed. The parent routes to the /mfa setup page. Receives the
   *  chosen display name so it can be persisted once a session exists. */
  onMfaSetupRequired?: (displayName: string) => void;
}

export default function ActivatePageOne({
  cognitologin,
  username,
  oldpassword,
  pageOneStateChangeHandler,
  updateDisplayName,
  onMfaSetupRequired,
}: ActivatePageOneProps) {
  const [displayName, setDisplayName] = useState("");
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleActivate = useCallback(async () => {
    const validate = new PasswordValidationClass();
    let validationError = validate.validateFirstPassword(password1, "", true);
    if (!validationError) {
      validationError = validate.validateSecondPassword(password1, password2, "");
    }
    if (!displayName.trim()) {
      validationError = "Please enter a preferred username";
    }

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");

      const result = await cognitologin.activateAccount(username, oldpassword || "", password1);

      if (result === EMPTY_STRING) {
        updateDisplayName(displayName);
        pageOneStateChangeHandler(password1);
      } else if (result === MFA_SETUP) {
        // Password set, but the pool requires MFA — enrol TOTP before anything
        // authenticated (the avatar step included). Skip the avatar page; the
        // user can set an avatar later from their profile.
        updateDisplayName(displayName);
        onMfaSetupRequired?.(displayName);
      } else {
        setErrorMessage(result);
      }
    } catch (activateError: any) {
      setErrorMessage(activateError?.message || String(activateError));
    } finally {
      setIsLoading(false);
    }
  }, [displayName, password1, password2, cognitologin, username, oldpassword, pageOneStateChangeHandler, updateDisplayName, onMfaSetupRequired]);

  const isFormValid = displayName.trim() && password1 && password2 && password1 === password2;

  return (
    <>
      {errorMessage && (
        <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
          {errorMessage}
        </div>
      )}

      <div className="space-y-4">
        <FormField label="Preferred Username" htmlFor="displayName" variant="dark">
          <Input
            id="displayName"
            type="text"
            variant="dark"
            placeholder="Choose a display name"
            value={displayName}
            onChange={(changeEvent) => { setDisplayName(changeEvent.target.value); setErrorMessage(""); }}
            disabled={isLoading}
            leftIcon={<User className="h-4 w-4" />}
          />
        </FormField>

        <FormField label="New Passphrase" htmlFor="password1" variant="dark">
          <Input
            id="password1"
            type="password"
            variant="dark"
            placeholder="Choose a passphrase"
            value={password1}
            onChange={(changeEvent) => { setPassword1(changeEvent.target.value); setErrorMessage(""); }}
            disabled={isLoading}
            leftIcon={<Lock className="h-4 w-4" />}
          />
        </FormField>

        {/* Live password-requirement checklist so the user knows the policy up
            front rather than guessing (mirrors the Cognito pool policy — see
            passwordValidation.ts PASSWORD_REQUIREMENTS). */}
        <ul className="space-y-1.5" aria-label="Passphrase requirements">
          {PASSWORD_REQUIREMENTS.map((requirement) => {
            const satisfied = requirement.isMet(password1);
            return (
              <li key={requirement.label} className="flex items-center gap-2 text-xs">
                <span className="flex h-3.5 w-3.5 items-center justify-center" aria-hidden="true">
                  {satisfied ? (
                    <Check className="h-3.5 w-3.5 text-green-400" />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-white/25" />
                  )}
                </span>
                <span className={satisfied ? "text-white/70" : "text-white/40"}>
                  {requirement.label}
                </span>
              </li>
            );
          })}
        </ul>

        <FormField label="Confirm Passphrase" htmlFor="password2" variant="dark">
          <Input
            id="password2"
            type="password"
            variant="dark"
            placeholder="Confirm your passphrase"
            value={password2}
            onChange={(changeEvent) => { setPassword2(changeEvent.target.value); setErrorMessage(""); }}
            disabled={isLoading}
            leftIcon={<Lock className="h-4 w-4" />}
          />
        </FormField>

        <Button
          onClick={handleActivate}
          disabled={isLoading || !isFormValid}
          className="w-full h-10 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Activating...
            </>
          ) : (
            "Activate"
          )}
        </Button>
      </div>

      {/* Standing reminder, not a consent gate. The contract is between NIMIS and
          the tenant organization — an invited user activating an account is not a
          party to it and signs nothing here, so a terms link would be neither
          accurate nor enforceable. What this user does need at this moment is the
          authorization and confidentiality boundary they are about to operate in. */}
      <div className="mt-6 pt-4 border-t border-white/[0.06]">
        <p className="text-xs leading-relaxed text-white/50">
          Your access is governed by the agreement between NIMIS and your
          organization. Only test systems your organization is authorized to
          test. Findings are confidential.
        </p>
      </div>
    </>
  );
}
